define((require) => {
    "use strict";

    const Class = require("shared/util/Class");
    const MathUtil  = require("shared/util/MathUtil").default;

    /**
     * Simple size object.
     *
     * @class Size
     *
     * @param {Number} width The width component of the size.
     * @param {Number} height The height component of the size.
     */
    const Size = Class.extend({

        constructor(width, height) {
            this.width = width;
            this.height = height;
        },

        /**
         * Shrinks the height or the width so that the new size obeys the specified aspect ratio.
         *
         * @param {Number} aspectRatio The aspect ratio is the width divided by the height.
         *
         * @return {Size} A new size that fits inside the current size.
         */
        shrinkToAspectRatio(aspectRatio) {
            const thisRatio = this.getAspectRatio();

            let w = this.width;
            let h = this.height;

            if (aspectRatio > thisRatio) {
                w = this.width;
                h = MathUtil.round(this.width / aspectRatio, 2);
            } else if (aspectRatio < thisRatio) {
                h = this.height;
                w = MathUtil.round(this.height * aspectRatio, 2);
            }

            return new Size(w, h);
        },

        /**
         * Grows the height or the width so that the new size obeys the specified aspect ratio.
         *
         * @param {Number} aspectRatio The aspect ratio is the width divided by the height.
         *
         * @return {Size} A new size that fits around the current size.
         */
        growToAspectRatio(aspectRatio) {
            const thisRatio = this.getAspectRatio();

            let w = this.width;
            let h = this.height;

            if (aspectRatio > thisRatio) {
                h = this.height;
                w = MathUtil.round(this.height * aspectRatio, 2);
            } else if (aspectRatio < thisRatio) {
                w = this.width;
                h = MathUtil.round(this.width / aspectRatio, 2);
            }

            return new Size(w, h);
        },

        /**
         * Returns this size's aspect ratio (the width divided by the height).
         *
         * @return {Number} This size's aspect ratio (the width divided by the height).
         */
        getAspectRatio() {
            return this.width / this.height;
        },

        /**
         * Returns a new size, representing the scaled version of this size, from the provided scale
         * instance.
         *
         * @param {Scale} scale The scale.
         *
         * @return {Size} The scaled size.
         */
        scale(scale) {
            return new Size(this.width * scale.x, this.height * scale.y);
        },

        area() {
            return this.width * this.height;
        },

        /**
         * Equals.
         */
        equals(size) {
            if (!size) {
                return false;
            }

            if (size === this) {
                return true;
            }

            if (size instanceof Size) {
                return this.width === size.width && this.height === size.height;
            }

            return false;
        }

    }, {

        /**
         * Static convenience method for creating a new <tt>Size</tt> instance.
         *
         * <pre>
         * var size = Size.from(100, 100);
         * </pre>
         *
         * If called with a single argument, it's expected to be an object specifying the bounds, e.g.:
         *
         * <pre>
         * var size = Size.from({width: 100, height: 100});
         * </pre>
         *
         * @param  {Number|Object} width  The width or an object specifying the width and height.
         * @param  {Number} height        The height.
         *
         * @return {Size} A new <tt>Size</tt> instance.
         */
        from(width, height) {
            if (arguments.length === 1) {
                const bounds = width;

                return new Size(bounds.width, bounds.height);
            }

            return new Size(width, height);
        }
    });

    return Size;
});



// WEBPACK FOOTER //
// ./src/shared/util/Size.js